// Voice Transcription Service - WebSocket Backend Integration
import { BASE_API } from '../axios';
import { TokenStorage } from '../utils/cookieStorage';

interface TranscriptionResult {
  text: string;
  isPartial: boolean;
  confidence?: number;
  timestamp?: number;
}

interface TranscriptionOptions {
  onTranscription: (result: TranscriptionResult) => void;
  onError: (error: Error) => void;
  onEnd: () => void;
  onStreamReady?: () => void;
}

interface WebSocketMessage {
  type: string;
  sessionId?: string;
  data?: any;
  message?: string;
  errorType?: string;
  status?: string;
  attempt?: number;
  maxAttempts?: number;
}

class VoiceTranscriptionService {
  private websocket: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private isRecording = false;
  private isConnected = false;
  private stream: MediaStream | null = null;
  private sessionId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  // Transcription state management
  private onTranscriptionCallback: ((result: TranscriptionResult) => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;
  private onEndCallback: (() => void) | null = null;
  private onStreamReadyCallback: (() => void) | null = null;

  constructor() {
    // Initialize audio worklet processor path
    this.loadAudioProcessor();
  }

  private async loadAudioProcessor() {
    try {
      if (this.audioContext) {
        // Load the AudioWorklet processor from public directory
        await this.audioContext.audioWorklet.addModule('/audioProcessor.js');
      }
    } catch (error) {
      console.warn('AudioWorklet not supported, using fallback approach');
    }
  }

  // Get microphone permission and setup audio processing
  async setupAudioCapture(): Promise<void> {
    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 2,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Create AudioContext and WorkletNode
      this.audioContext = new AudioContext({ sampleRate: 16000 });

      // Load audio processor worklet from public directory
      await this.audioContext.audioWorklet.addModule('/audioProcessor.js');

      // Create the worklet node
      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor');

      // Handle audio data from worklet
      this.audioWorkletNode.port.onmessage = (event) => {
        if (event.data.type === 'audioData' && this.isRecording && this.websocket) {
          // Send binary audio data to backend via WebSocket
          this.websocket.send(event.data.data);
        }
      };

      // Connect audio stream to worklet
      const source = this.audioContext.createMediaStreamSource(this.stream);
      source.connect(this.audioWorkletNode);

      console.log('Audio capture setup completed');
    } catch (error) {
      console.error('Error setting up audio capture:', error);
      throw new Error(`Audio setup failed: ${error.message}`);
    }
  }

  // Connect to backend WebSocket with JWT authentication
  private async connectWebSocket(): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.websocket && (this.websocket.readyState === WebSocket.CONNECTING || this.websocket.readyState === WebSocket.OPEN)) {
      console.log('WebSocket already connecting or connected, skipping...');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        // Get JWT token from cookies using TokenStorage
        const token = TokenStorage.getAccessToken();
        if (!token) {
          reject(new Error('No authentication token found'));
          return;
        }

        // Construct WebSocket URL (convert HTTP/HTTPS to WS/WSS)
        const wsUrl = BASE_API.replace('http://', 'ws://').replace('https://', 'wss://') +
                     `/transcription-stream?token=${token}`;

        console.log(`Connecting to WebSocket: ${wsUrl.substring(0,35)} ...`);

        this.websocket = new WebSocket(wsUrl);

        this.websocket.onopen = () => {
          console.log('WebSocket connected successfully');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          resolve();
        };

        this.websocket.onmessage = (event) => {
          try {
            this.handleWebSocketMessage(event.data);
          } catch (error) {
            console.error('Error handling WebSocket message:', error);
            // Don't close connection on message handling errors
          }
        };

        this.websocket.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.isConnected = false;
          this.stopHeartbeat();
          this.handleConnectionClose();
        };

        this.websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.handleConnectionError(new Error('WebSocket connection error'));
          reject(error);
        };

      } catch (error) {
        console.error('Error creating WebSocket connection:', error);
        reject(error);
      }
    });
  }

  // Handle WebSocket messages from backend
  private handleWebSocketMessage(data: string) {
    try {
      const message: WebSocketMessage = JSON.parse(data);

      switch (message.type) {
        case 'connection':
          this.sessionId = message.sessionId || null;
          console.log('WebSocket connection established with session:', this.sessionId);
          break;

        case 'transcription_started':
          console.log('Transcription started successfully');
          if (this.onStreamReadyCallback) {
            this.onStreamReadyCallback();
          }
          break;

        case 'transcription_result':
          if (message.data && this.onTranscriptionCallback) {
            this.onTranscriptionCallback({
              text: message.data.text,
              isPartial: message.data.isPartial,
              confidence: message.data.confidence,
              timestamp: message.data.timestamp,
            });
          }
          break;

        case 'transcription_stopped':
          console.log('Transcription stopped by backend');
          if (this.onEndCallback) {
            this.onEndCallback(); // This calls handleTranscriptionEnd() → setState('idle')
          }
          // Cleanup after callback to ensure UI state is updated first
          this.cleanup();
          break;

        case 'reconnecting':
          console.log(`Attempting to reconnect (${message.attempt}/${message.maxAttempts})`);
          break;

        case 'reconnected':
          console.log('Successfully reconnected to transcription service');
          break;

        case 'error':
          console.error('Transcription error:', message.errorType, message.message);

          // Handle specific error types
          if (message.errorType === 'pool_not_initialized') {
            console.error('CRITICAL: AWS transcription service not initialized on backend');
            this.cleanup();
          }

          if (this.onErrorCallback) {
            this.onErrorCallback(new Error(`${message.errorType}: ${message.message}`));
          }
          break;

        case 'heartbeat_ack':
          // Heartbeat acknowledged
          break;

        default:
          console.warn('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  // Handle WebSocket connection close
  private handleConnectionClose() {
    if (this.isRecording && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.attemptReconnection();
    }
  }

  // Handle connection errors with auto-reconnect
  private handleConnectionError(error: Error) {
    console.error('Connection error:', error);

    if (this.onErrorCallback && this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.onErrorCallback(error);
    } else if (this.isRecording) {
      this.attemptReconnection();
    }
  }

  // Attempt to reconnect WebSocket
  private async attemptReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts exceeded');
      if (this.onErrorCallback) {
        this.onErrorCallback(new Error('Failed to reconnect to transcription service'));
      }
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 5000);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(async () => {
      try {
        await this.connectWebSocket();

        // Restart transcription if we were recording
        if (this.isRecording) {
          this.sendMessage({ type: 'start_transcription' });
        }
      } catch (error) {
        console.error('Reconnection failed:', error);
        this.handleConnectionError(error);
      }
    }, delay);
  }

  // Send message to backend via WebSocket
  private sendMessage(message: any) {
    if (this.websocket && this.isConnected) {
      this.websocket.send(JSON.stringify(message));
    }
  }

  // Start heartbeat to keep connection alive
  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage({ type: 'heartbeat' });
    }, 25000); // Every 25 seconds
  }

  // Stop heartbeat
  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Start transcription process
  async startTranscription(options: TranscriptionOptions): Promise<void> {
    try {
      // Store callbacks
      this.onTranscriptionCallback = options.onTranscription;
      this.onErrorCallback = options.onError;
      this.onEndCallback = options.onEnd;
      this.onStreamReadyCallback = options.onStreamReady;

      // Setup audio capture
      await this.setupAudioCapture();

      // Connect to WebSocket
      await this.connectWebSocket();

      // Start recording
      this.isRecording = true;

      // Resume audio context if suspended
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Send start transcription message
      this.sendMessage({ type: 'start_transcription' });

    } catch (error) {
      console.error('Error starting transcription:', error);
      this.cleanup();
      if (options.onError) {
        options.onError(error);
      }
      throw error;
    }
  }

  // Stop transcription process
  async stopTranscription(): Promise<void> {
    try {
      console.log('Stopping transcription...');

      this.isRecording = false;

      // Send stop message to backend and wait for confirmation
      if (this.websocket && this.isConnected) {
        this.sendMessage({ type: 'stop_transcription' });
        console.log('Stop message sent to backend, waiting for confirmation...');

        // Don't cleanup immediately - wait for backend 'transcription_stopped' message
        // which will trigger onEndCallback and then cleanup
      } else {
        // No connection - cleanup immediately
        console.log('No WebSocket connection, cleaning up immediately');
        this.cleanup();
      }

      console.log('Transcription stop initiated successfully');

    } catch (error) {
      console.error('Error stopping transcription:', error);
      this.cleanup(); // Cleanup on error
      throw error;
    }
  }

  // Cleanup resources
  private cleanup(): void {
    console.log('Cleaning up transcription service resources...');

    this.isRecording = false;
    this.isConnected = false;

    // Stop heartbeat
    this.stopHeartbeat();

    // Close WebSocket
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    // Stop audio stream
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Disconnect audio nodes
    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }

    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clear callbacks
    this.onTranscriptionCallback = null;
    this.onErrorCallback = null;
    this.onEndCallback = null;
    this.onStreamReadyCallback = null;

    // Reset connection state
    this.sessionId = null;
    this.reconnectAttempts = 0;

    console.log('Cleanup completed');
  }

  // Check if currently transcribing
  isTranscribing(): boolean {
    return this.isRecording && this.isConnected;
  }

  // Get current session information
  getSessionInfo() {
    return {
      isRecording: this.isRecording,
      isConnected: this.isConnected,
      sessionId: this.sessionId,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  // Static method to check browser support for voice transcription
  static isSupported(): boolean {
    try {
      // Check for required Web APIs that must exist before we can even attempt transcription
      const hasMediaDevices = !!(navigator?.mediaDevices?.getUserMedia);
      const hasAudioContext = !!(window.AudioContext || (window as any).webkitAudioContext);
      const hasWebSocketSupport = typeof WebSocket !== 'undefined'; // Safer way to check WebSocket availability

      // AudioWorklet is needed for real-time audio processing
      let hasAudioWorklet = false;
      try {
        // Safer way to check AudioWorklet support without accessing prototype directly
        if (window.AudioContext) {
          const tempContext = new AudioContext();
          hasAudioWorklet = !!(tempContext.audioWorklet);
          tempContext.close(); // Clean up the temporary context
        }
      } catch (e) {
        // AudioWorklet might not be available in all browsers
        hasAudioWorklet = false;
      }

      return hasMediaDevices && hasAudioContext && hasWebSocketSupport && hasAudioWorklet;
    } catch (error) {
      console.warn('Error checking voice transcription support:', error);
      return false;
    }
  }
}

// Singleton instance to prevent multiple WebSocket connections
let singletonInstance: VoiceTranscriptionService | null = null;

export const getVoiceTranscriptionService = (): VoiceTranscriptionService => {
  if (!singletonInstance) {
    console.log('Creating new VoiceTranscriptionService singleton instance');
    singletonInstance = new VoiceTranscriptionService();
  } else {
    console.log('Reusing existing VoiceTranscriptionService singleton instance');
  }
  return singletonInstance;
};

// Export singleton instance
export const voiceTranscriptionService = getVoiceTranscriptionService();
export default VoiceTranscriptionService;